import React, { ReactNode } from "react";
import { FormikProps } from "formik";
import ATMBreadCrumbs, {
  BreadcrumbType,
} from "src/components/UI/atoms/ATMBreadCrumbs/ATMBreadCrumbs";
import ATMLoadingButton from "src/components/UI/atoms/ATMLoadingButton/ATMLoadingButton";
import { LogoInitialValues } from "./AddLogoWrapper";
import ATMFilePickerWrapper from "src/components/UI/atoms/formFields/ATMFileUploader/ATMFileUploaderWrapper";
import { RxDashboard } from "react-icons/rx";
import { IoPersonCircleOutline } from "react-icons/io5";

type Props = {
  formikProps: FormikProps<LogoInitialValues>;
};

// Form Section Heading
const FormSectionHeading = ({ children }: { children: ReactNode }) => {
  return (
    <div className="border-l-[3px] border-primary-main px-3 py-2 text-slate-700 font-medium text-[15px] sticky top-0 bg-white z-50">
      {children}
    </div>
  );
};

const breadcrumbs: BreadcrumbType[] = [
  {
    label: "Employer Certificate Logo List",
    path: "/logo-upload",
    icon: IoPersonCircleOutline,
  },
  {
    label: "Employer Certificate Logo Add",
    icon: RxDashboard,
  },
];

const AddLogo = ({ formikProps }: Props) => {
  const { values, setFieldValue, isSubmitting } = formikProps;

  return (
    <div className="h-full py-5 px-4 flex flex-col gap-3 ">
      <ATMBreadCrumbs breadcrumbs={breadcrumbs} />
      <div className="shadow-sm flex flex-col overflow-auto  border rounded p-3 bg-white relative">
        <div className="grow overflow-auto">
          {/* Logo Information */}
          <div className="border-slate-200 pb-3">
            <FormSectionHeading>LOGO INFORMATION</FormSectionHeading>

            <div className="px-3 py-5 grid grid-cols-3 gap-5">
              {/* Image */}
              <ATMFilePickerWrapper
                name="image"
                selectedFile={values.image}
                onSelect={(newValue) => setFieldValue("image", newValue)}
                label="Image"
              />
            </div>
          </div>

          {/* DSA (175) Employer Certificate issuer — both optional */}
          <div className="border-slate-200 pb-3">
            <FormSectionHeading>DSA CERTIFICATE (OPTIONAL)</FormSectionHeading>

            <div className="px-3 py-5 grid grid-cols-3 gap-5">
              {/* Firm name */}
              <div className="flex flex-col gap-1">
                <label className="font-medium text-primary-main text-sm">
                  Firm Name
                </label>
                <input
                  name="firmName"
                  value={values.firmName}
                  maxLength={100}
                  onChange={(e) =>
                    setFieldValue("firmName", e.target.value.toUpperCase())
                  }
                  placeholder="e.g. LAKSHYA ENTERPRISES"
                  className="w-full border border-slate-300 rounded px-3 py-2 text-sm focus:outline-none focus:border-primary-main"
                />
                <span className="text-xs text-slate-500">
                  Printed as "C/O firm name" on DSA PAN forms and as the issuer
                  on the Employer Certificate.
                </span>
              </div>

              {/* Seal */}
              <div className="flex flex-col gap-1">
                <ATMFilePickerWrapper
                  name="seal"
                  selectedFile={values.seal}
                  onSelect={(newValue) => setFieldValue("seal", newValue)}
                  label="Seal"
                  accept="image/png,image/jpeg,image/webp"
                />
                <span className="text-xs text-slate-500">
                  PNG with a transparent background. Printed over the
                  applicant's photo.
                </span>
              </div>
            </div>

            <div className="px-3 text-xs text-slate-500">
              Each DSA application is given one logo at random and keeps it:
              its logo, firm name and seal print on that application's PAN form
              and Employer Certificate.
            </div>
          </div>
        </div>

        <div className="flex justify-end">
          <div>
            <ATMLoadingButton type="submit" isLoading={isSubmitting}>
              Add Employer Certificate Logo
            </ATMLoadingButton>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddLogo;
